"use client";

import { animate, motion, useInView, useReducedMotion } from "framer-motion";
import { useEffect, useMemo, useRef, useState } from "react";

const TICKER_EASE = [0.22, 1, 0.36, 1];
const NUMBER_FORMATTER = new Intl.NumberFormat("en-GB");

function parseMetric(metric) {
  const value = String(metric).trim();
  const match = value.match(/^([^0-9]*)(\d[\d,]*)(.*)$/);

  if (!match) {
    return null;
  }

  const [, prefix, numericValue, suffix] = match;

  return {
    prefix,
    suffix,
    endValue: Number(numericValue.replace(/,/g, "")),
  };
}

function formatMetric(parsedMetric, value) {
  return `${parsedMetric.prefix}${NUMBER_FORMATTER.format(value)}${parsedMetric.suffix}`;
}

export default function MetricTicker({
  metric,
  description,
  delay = 0,
  className = "",
  valueClassName = "",
  descriptionClassName = "",
}) {
  const containerRef = useRef(null);
  const isInView = useInView(containerRef, { once: true, amount: 0.55 });
  const shouldReduceMotion = useReducedMotion();
  const parsedMetric = useMemo(() => parseMetric(metric), [metric]);
  const [displayValue, setDisplayValue] = useState(() => {
    if (!parsedMetric || shouldReduceMotion) {
      return metric;
    }

    return formatMetric(parsedMetric, 0);
  });

  useEffect(() => {
    if (!parsedMetric || shouldReduceMotion) {
      setDisplayValue(metric);
      return;
    }

    if (!isInView) {
      setDisplayValue(formatMetric(parsedMetric, 0));
      return;
    }

    const controls = animate(0, parsedMetric.endValue, {
      delay,
      duration: 1.15,
      ease: TICKER_EASE,
      onUpdate(latestValue) {
        const roundedValue =
          latestValue < parsedMetric.endValue
            ? Math.floor(latestValue)
            : parsedMetric.endValue;

        setDisplayValue(formatMetric(parsedMetric, roundedValue));
      },
      onComplete() {
        setDisplayValue(formatMetric(parsedMetric, parsedMetric.endValue));
      },
    });

    return () => {
      controls.stop();
    };
  }, [delay, isInView, metric, parsedMetric, shouldReduceMotion]);

  return (
    <motion.div
      ref={containerRef}
      initial={
        shouldReduceMotion ? false : { opacity: 0, y: 18, filter: "blur(8px)" }
      }
      whileInView={
        shouldReduceMotion
          ? undefined
          : { opacity: 1, y: 0, filter: "blur(0px)" }
      }
      viewport={{ once: true, amount: 0.45 }}
      transition={
        shouldReduceMotion
          ? undefined
          : { duration: 3, delay, ease: TICKER_EASE }
      }
      className={className}
    >
      <p
        aria-label={metric}
        className={valueClassName}
        style={{ letterSpacing: "-0.03em" }}
      >
        {displayValue}
      </p>
      <p className={descriptionClassName}>{description}</p>
    </motion.div>
  );
}
